import { useState } from 'react';

export default function Services() {
  const [selectedService, setSelectedService] = useState<number | null>(null);

  const services = [
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services to modernize your IT environment',
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]',
      details: {
        overview: 'Transform your business with enterprise-grade cloud solutions designed for security, scalability, and performance.',
        features: [
          'Secure cloud migration and deployment',
          'Hybrid and multi-cloud architecture',
          'Cloud security and compliance',
          'Disaster recovery and backup solutions',
          '24/7 cloud monitoring and management'
        ],
        benefits: 'Reduce infrastructure costs by up to 40% while improving system reliability and enabling remote workforce capabilities.'
      }
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Cybersecurity',
      description: 'Comprehensive security solutions to protect your business from evolving threats',
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]',
      details: {
        overview: 'Protect your organization with military-grade cybersecurity solutions tailored for defense contractors and government agencies.',
        features: [
          'CMMC compliance and certification support',
          'Threat detection and incident response',
          'Security awareness training',
          'Vulnerability assessments and penetration testing',
          'Zero-trust architecture implementation'
        ],
        benefits: 'Achieve compliance with federal security requirements while protecting sensitive data from advanced persistent threats.'
      }
    },
    {
      icon: 'ri-brain-line',
      title: 'AI & Analytics',
      description: 'Harness the power of artificial intelligence and data analytics for smarter decisions',
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]',
      details: {
        overview: 'Leverage cutting-edge AI and machine learning to gain actionable insights and automate complex processes.',
        features: [
          'Predictive analytics and forecasting',
          'Machine learning model development',
          'Business intelligence dashboards',
          'Natural language processing solutions',
          'Automated reporting and insights'
        ],
        benefits: 'Make data-driven decisions faster and uncover hidden opportunities with advanced analytics capabilities.'
      }
    },
    {
      icon: 'ri-code-box-line',
      title: 'Custom Development',
      description: 'Tailored software solutions designed to meet your unique business requirements',
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]',
      details: {
        overview: 'Build custom applications that streamline operations and give you a competitive edge in your industry.',
        features: [
          'Full-stack web and mobile development',
          'API development and integration',
          'Legacy system modernization',
          'Secure application development',
          'Agile development methodology'
        ],
        benefits: 'Get software that fits your exact needs, integrates seamlessly with existing systems, and scales with your growth.'
      }
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'IT Support',
      description: '24/7 technical support and maintenance to keep your systems running smoothly',
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]',
      details: {
        overview: 'Ensure maximum uptime and productivity with our comprehensive managed IT support services.',
        features: [
          '24/7/365 help desk support',
          'Proactive system monitoring',
          'Patch management and updates',
          'Hardware and software troubleshooting',
          'On-site and remote support options'
        ],
        benefits: 'Minimize downtime and reduce IT costs with predictable monthly pricing and expert technical support.'
      }
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Digital Strategy',
      description: 'Strategic consulting to align technology with your business objectives',
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]',
      details: {
        overview: 'Develop a comprehensive technology roadmap that drives innovation and supports your long-term business goals.',
        features: [
          'IT infrastructure assessment',
          'Technology roadmap development',
          'Digital transformation planning',
          'Vendor evaluation and selection',
          'IT budget optimization'
        ],
        benefits: 'Align your technology investments with business objectives and stay ahead of industry trends.'
      }
    }
  ];

  const closeModal = () => setSelectedService(null);

  return (
    <section id="services" className="py-12 sm:py-24 px-4 sm:px-8 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <i className="ri-shape-line text-[#0066CC]"></i>
            <span className="text-sm font-semibold text-[#0066CC] uppercase tracking-wider whitespace-nowrap">
              Our Services
            </span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-[#1A2332] mb-4">
            Comprehensive IT Solutions
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`${service.color} p-6 sm:p-10 rounded-2xl sm:rounded-3xl hover:shadow-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer`}
            >
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${service.iconColor} bg-white flex items-center justify-center mb-4 sm:mb-6`}>
                <i className={`${service.icon} text-xl sm:text-2xl`}></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1A2332] mb-3 sm:mb-4">
                {service.title}
              </h3>
              <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                {service.description}
              </p>
              <button 
                onClick={() => setSelectedService(index)}
                className="inline-flex items-center text-[#0066CC] font-semibold hover:gap-2 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                Learn More <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Service Detail Modal */}
      {selectedService !== null && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-8">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-xl ${services[selectedService].iconColor} bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] flex items-center justify-center`}>
                    <i className={`${services[selectedService].icon} text-3xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A2332]">
                      {services[selectedService].title}
                    </h3>
                    <p className="text-gray-500 text-sm">Professional IT Service</p>
                  </div>
                </div>
                <button 
                  onClick={closeModal}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-gray-500"></i>
                </button>
              </div>

              {/* Overview */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-[#1A2332] mb-2">Overview</h4>
                <p className="text-gray-600 leading-relaxed">
                  {services[selectedService].details.overview}
                </p>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-[#1A2332] mb-3">Key Features</h4>
                <ul className="space-y-3">
                  {services[selectedService].details.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#0066CC]/10 mt-0.5">
                        <i className="ri-check-line text-[#0066CC] text-sm"></i>
                      </div>
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] rounded-xl p-5 mb-6">
                <h4 className="text-lg font-semibold text-[#1A2332] mb-2">Benefits</h4>
                <p className="text-gray-700 leading-relaxed">
                  {services[selectedService].details.benefits}
                </p>
              </div>

              {/* CTA */}
              <div className="flex gap-4">
                <a 
                  href="#booking"
                  onClick={closeModal}
                  className="flex-1 bg-[#0066CC] text-white py-3 px-6 rounded-lg font-semibold text-center hover:bg-[#0055AA] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Started
                </a>
                <button 
                  onClick={closeModal}
                  className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
